import type { IconType } from 'react-icons';
// `counterParts` and `StatFigure` belong to the stats band, commented out below.
import { ABOUT, type ProgrammeService } from '@/data/about';
import {
    FaScaleBalanced,
    FaPeopleGroup,
    FaChartColumn,
    FaShieldHalved,
    FaGavel,
    FaHandshakeAngle,
    FaNoteSticky,
} from 'react-icons/fa6';
import { Reveal } from '@components/motion';
import FigmaFrame from '@components/landing/FigmaFrame';
import { Img } from '@/components/Img';

const STATUE = '/landing/tackled-impact/statue.webp';

/** The marks a programme card can name in the CMS. Anything unknown gets the handshake. */
const ICONS: Record<string, IconType> = {
    Scale: FaScaleBalanced,
    Gavel: FaGavel,
    ChartBar: FaChartColumn,
    Shield: FaShieldHalved,
    Users: FaPeopleGroup,
    Handshake: FaHandshakeAngle,
    Note: FaNoteSticky,
};
const iconFor = (name: string): IconType => ICONS[name] ?? FaHandshakeAngle;

/** Read at render so the hydrated document is what shows. */
// const stats = (): StatFigure[] => ABOUT.programmes.stats;
const services = (): ProgrammeService[] => ABOUT.programmes.services;

// The stats band is commented out on the page; this is what drew each figure.
// /** "10M+" — dark figure with the orange plus (when the figure carries one), over its caption. */
// function StatBlock({ stat, size = 'desktop' }: { stat: StatFigure; size?: 'desktop' | 'mobile' }) {
//     const { figure, plus } = counterParts(stat.value);
//     const desktop = size === 'desktop';
//     return (
//         <div>
//             <p
//                 className={
//                     desktop
//                         ? 'whitespace-nowrap font-primary text-[72px] font-bold leading-none tracking-[-2px] text-[#050505]'
//                         : 'whitespace-nowrap font-primary text-[52px] font-bold leading-none tracking-[-1.5px] text-[#050505]'
//                 }
//             >
//                 {figure}
//                 {plus && <span className="text-[#f60]">{plus}</span>}
//             </p>
//             <p
//                 className={
//                     desktop
//                         ? 'mt-[18px] text-[15px] font-normal leading-[1.5] text-[#555]'
//                         : 'mt-3 text-[14px] font-normal leading-[1.5] text-[#555]'
//                 }
//             >
//                 {stat.caption}
//             </p>
//         </div>
//     );
// }

/** The orange pill and two-line heading on the dark panel, written on the About page. */
function PanelHeading({ size }: { size: 'desktop' | 'mobile' }) {
    const desktop = size === 'desktop';
    return (
        <>
            {ABOUT.programmes.eyebrow && (
                <span
                    className={
                        desktop
                            ? 'absolute left-[129px] top-[93px] flex h-[38px] items-center rounded-full bg-[#f60] px-[22px] text-[16px] font-bold text-[#050505]'
                            : 'flex h-[36px] w-fit items-center rounded-full bg-[#f60] px-5 text-[15px] font-bold text-[#050505]'
                    }
                >
                    {ABOUT.programmes.eyebrow}
                </span>
            )}
            <div
                className={
                    desktop
                        ? 'absolute left-[129px] top-[155px] font-primary text-[56px] leading-[1.25] tracking-[-1.5px]'
                        : 'mt-6 font-primary text-[40px] leading-[1.2] tracking-[-1px]'
                }
            >
                {ABOUT.programmes.titleLead && (
                    <p className="mb-0 font-light text-[#6c6c6c]">{ABOUT.programmes.titleLead}</p>
                )}
                <p className="font-bold text-white">{ABOUT.programmes.titleBold}</p>
            </div>
        </>
    );
}

/** Icon tile + title + copy, as used inside the black services panel. */
function ServiceCard({ service, className }: { service: ProgrammeService; className?: string }) {
    const Icon = iconFor(service.icon);
    return (
        <div className={className}>
            <span className="flex size-[58px] items-center justify-center rounded-[14px] bg-[#262626]">
                <Icon className="size-[26px] text-white" />
            </span>
            <p className="mt-[24px] text-[22px] font-semibold leading-[1.35] text-white">{service.title}</p>
            <p className="mt-[14px] text-[15px] font-normal leading-[1.55] text-[#9a9a9a]">{service.body}</p>
        </div>
    );
}

function OurProgramsDesktop() {
    return (
        <FigmaFrame designWidth={1920} designHeight={1100} className="w-full">
            <div className="relative h-[1100px] w-[1920px] bg-[#e8e8e8]">
                {/* Statue image — tucks behind the black panel */}
                <Reveal
                    as="div"
                    once
                    variant="fade-right"
                    duration={900}
                    className="absolute left-[254px] top-[86px] h-[400px] w-[490px] overflow-hidden rounded-[16px] bg-[#d9d9d9]"
                >
                    <Img
                        src={ABOUT.programmes.image || STATUE}
                        alt={ABOUT.programmes.image ? ABOUT.programmes.imageAlt : ''}
                        className="size-full object-cover object-center grayscale"
                    />
                </Reveal>

                {/* Intro paragraph */}
                <Reveal
                    as="p"
                    once
                    variant="fade-up"
                    delay={120}
                    className="absolute left-[803px] top-[137px] w-[700px] text-[19px] font-normal leading-[1.7] text-[#333]"
                >
                    {ABOUT.programmes.intro}
                </Reveal>

                {/* Stats row — off for now. Restore with StatBlock, `stats()` and the imports above.
                <div className="absolute left-[803px] top-[265px] flex w-[1000px] gap-[30px]">
                    {stats().map((stat, i) => (
                        <Reveal key={i} once variant="fade-up" delay={200 + i * 100} className="w-[300px] shrink-0">
                            <StatBlock stat={stat} />
                        </Reveal>
                    ))}
                </div>
                */}

                {/* Black services panel (overlaps the image) */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={200}
                    className="absolute left-[143px] top-[438px] h-[562px] w-[1640px] rounded-[40px] bg-[#050505]"
                >
                    <PanelHeading size="desktop" />

                    {/* Services grid */}
                    <div className="absolute left-[588px] top-[52px] grid w-[964px] grid-cols-3 gap-x-[107px] gap-y-[72px]">
                        {services().map((service, i) => (
                            <Reveal key={i} once variant="fade-up" delay={i * 80}>
                                <ServiceCard service={service} className="w-[250px]" />
                            </Reveal>
                        ))}
                    </div>
                </Reveal>
            </div>
        </FigmaFrame>
    );
}

function OurProgramsMobile() {
    return (
        <section className="bg-[#e8e8e8] px-5 py-14">
            <div className="h-[280px] w-full overflow-hidden rounded-[16px] bg-[#d9d9d9]">
                <Img
                    src={ABOUT.programmes.image || STATUE}
                    alt={ABOUT.programmes.image ? ABOUT.programmes.imageAlt : ''}
                    className="size-full object-cover object-center grayscale"
                />
            </div>

            <p className="mt-8 text-[15px] font-normal leading-[1.7] text-[#333]">{ABOUT.programmes.intro}</p>

            {/* Stats — off for now, as on desktop.
            <div className="mt-8 flex flex-col gap-8">
                {stats().map((stat, i) => (
                    <StatBlock key={i} stat={stat} size="mobile" />
                ))}
            </div>
            */}

            <div className="mt-12 rounded-[28px] bg-[#050505] px-6 py-10">
                <PanelHeading size="mobile" />

                <div className="mt-10 flex flex-col gap-9">
                    {services().map((service, i) => (
                        <ServiceCard key={i} service={service} />
                    ))}
                </div>
            </div>
        </section>
    );
}

export default function AboutOurPrograms() {
    return (
        <>
            <div className="hidden lg:block">
                <OurProgramsDesktop />
            </div>
            <div className="lg:hidden">
                <OurProgramsMobile />
            </div>
        </>
    );
}
